import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { supabase, setSupabaseAccessToken } from "@/lib/supabase";

const AuthContext = createContext(null);

// Derive aal from the JWT SYNCHRONOUSLY. Never call supabase.auth.mfa.getAuthenticatorAssuranceLevel()
// or getSession() from inside an onAuthStateChange callback — those acquire supabase-js v2's
// LockManager and re-enter while the lib is mid-notify during mfa.verify(), deadlocking the flow.
function isAal2(token) {
  try {
    return JSON.parse(atob(token.split(".")[1])).aal === "aal2";
  } catch {
    return false;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=guest, obj=authenticated

  const refreshUser = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(false);
      return false;
    }
  };

  useEffect(() => {
    // Rely solely on onAuthStateChange (it emits INITIAL_SESSION on subscribe, then SIGNED_IN /
    // TOKEN_REFRESHED / MFA verify / SIGNED_OUT). The callback stays lock-free.
    const sub = supabase?.auth.onAuthStateChange((_event, session) => {
      if (session?.access_token && isAal2(session.access_token)) refreshUser();
      else setUser(false);
    });
    return () => sub?.data?.subscription?.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Drives the current Supabase session to aal2 (TOTP). Returns {status:"done",user} or
  // {status:"mfa", stage, factorId, challengeId, qr?, secret?}. Called from submit handlers
  // (NOT from an auth-state callback), so the mfa.* lock calls here are safe.
  const ensureMfa = async (session) => {
    let token = session?.access_token;
    if (!token) {
      const { data } = await supabase.auth.getSession();
      token = data?.session?.access_token;
    }
    if (token && isAal2(token)) return { status: "done", user: await refreshUser() };
    const { data: factors } = await supabase.auth.mfa.listFactors();
    const verified = (factors?.totp || []).find((f) => f.status === "verified");
    if (verified) {
      const { data: ch, error } = await supabase.auth.mfa.challenge({ factorId: verified.id });
      if (error) throw error;
      return { status: "mfa", stage: "verify", factorId: verified.id, challengeId: ch.id };
    }
    // No verified factor. Clear any stale UNVERIFIED factors (e.g. from an abandoned enroll) so a
    // fresh enrollment isn't blocked and the user isn't stranded on a "verify" screen for a factor
    // their authenticator never actually stored.
    for (const f of (factors?.totp || []).filter((f) => f.status !== "verified")) {
      try { await supabase.auth.mfa.unenroll({ factorId: f.id }); } catch { /* best-effort */ }
    }
    const { data: en, error } = await supabase.auth.mfa.enroll({ factorType: "totp", issuer: "Upland Group", friendlyName: `Upland Group (${new Date().toISOString().slice(0, 10)})` });
    if (error) throw error;
    return { status: "mfa", stage: "enroll", factorId: en.id, qr: en.totp.qr_code, secret: en.totp.secret };
  };

  const signIn = async (email, password, captchaToken) => {
    if (!supabase) throw new Error("supabase-unavailable");
    const options = captchaToken ? { captchaToken } : undefined;
    const { data, error } = await supabase.auth.signInWithPassword({ email, password, options });
    if (error) throw error; // genuine bad-credentials error
    // Any failure past this point is a post-auth (MFA) problem, NOT a wrong password — tag it so
    // the login screens don't mislabel a correct password as "Invalid email or password".
    try {
      return await ensureMfa(data.session);
    } catch (e) {
      if (e && typeof e === "object") e.postAuth = true;
      throw e;
    }
  };

  // Send our OWN branded recovery email via the backend (Resend + Supabase recovery link) — keeps
  // auth emails first-party on uplandgroupllc.com and off Supabase's rate-limited default SMTP.
  const sendPasswordReset = async (email) => {
    await api.post("/auth/request-password-reset", { email });
  };

  // Set a new password on the recovery session, then drive to aal2.
  const updatePassword = async (password) => {
    if (!supabase) throw new Error("supabase-unavailable");
    const { data, error } = await supabase.auth.updateUser({ password });
    if (error) throw error;
    return ensureMfa(data?.session);
  };

  const hasRecoverySession = async () => {
    if (!supabase) return false;
    const { data } = await supabase.auth.getSession();
    return !!data?.session;
  };

  // Submit the 6-digit TOTP code. Ends the session at aal2, then loads the app user. We prime the
  // cached token from mfa.verify's response so /auth/me uses the aal2 token immediately.
  const verifyMfa = async ({ stage, factorId, challengeId, code }) => {
    let chId = challengeId;
    if (stage === "enroll") {
      const { data: ch, error } = await supabase.auth.mfa.challenge({ factorId });
      if (error) throw error;
      chId = ch.id;
    }
    const { data, error } = await supabase.auth.mfa.verify({ factorId, challengeId: chId, code });
    if (error) throw error;
    if (data?.access_token) setSupabaseAccessToken(data.access_token);
    return await refreshUser();
  };

  const logout = async () => {
    try { await supabase?.auth.signOut(); } catch { /* best-effort */ }
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, signIn, ensureMfa, verifyMfa, logout, refreshUser, sendPasswordReset, updatePassword, hasRecoverySession }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
